// ─── Servicio unificado de registro de ventas (colección Sales) ──────────────
// Todos los flujos de cobro (Nueva Venta en Caja y pagos rápidos desde órdenes
// de laboratorio, imagen y agenda) deben registrar las ventas con el mismo
// esquema, para que Caja, Informe Financiero y Libro de Ventas cuadren:
//  - referencias: presupuestoId/presupuestoNumber, orderId/orderType/orderNumber
//  - descuentos del presupuesto reflejados en items y descuentoTotal
//  - sucursal (branchId) y turno de caja (turnoId) cuando hay turno abierto
//  - acumuladores de turnosCaja actualizados (totales por medio, descuentos)

import {
  collection, doc, getDoc, writeBatch, serverTimestamp, increment,
} from 'firebase/firestore';
import { db } from '../firebase';
import { getTurnoActivo } from './cajaService';
import { netoDesdeBruto, ivaDesdeBruto } from '../utils/finance';

// Colección de la orden de origen según orderType del presupuesto
export const ORDER_COLLECTION_BY_TYPE = {
  labOrder: 'labOrders',
  imagingOrder: 'imagingOrders',
  surgeryRequest: 'surgeryRequests',
  hospitalizationOrder: 'hospitalizationOrders',
  onlineBooking: 'onlineBookings',
};

// Tipo de línea del ticket según orderType (ver TIPOS_ITEM en utils/finance)
export const TIPO_ITEM_BY_ORDER_TYPE = {
  labOrder: 'laboratorio',
  imagingOrder: 'otro',
  surgeryRequest: 'cirugia',
  hospitalizationOrder: 'hospitalizacion',
  onlineBooking: 'consulta',
  vacuna: 'otro',
  antiparasitario: 'farmacia',
};

// Normaliza los medios de pago del PaymentDialog al catálogo de MEDIOS_PAGO
const MEDIO_MAP = {
  tarjeta_debito: 'debito',
  tarjeta_credito: 'credito',
  webpay: 'webpay',
  transferencia: 'transferencia',
  deposito: 'otro',
  efectivo: 'efectivo',
};

const CAMPO_TURNO_POR_MEDIO = {
  efectivo: 'totalEfectivo',
  debito: 'totalDebito',
  credito: 'totalCredito',
  transferencia: 'totalTransferencia',
};

/**
 * Registra una venta unificada desde un flujo de pago rápido (PaymentDialog).
 * Si la orden tiene presupuesto asociado, los ítems y totales (incluidos los
 * descuentos de convenio) se toman del presupuesto; si no, de fallbackItems.
 * Actualiza en un batch atómico: Sales + orden + presupuesto + turnosCaja.
 *
 * @returns {Promise<{id: string, total: number, descuentoTotal: number}>}
 */
export async function registrarVentaOrden({
  presupuestoId = null,
  orderId = null,
  orderCollection = null,
  orderType = '',
  orderNumber = '',
  patientId = '',
  patientName = '',
  patientSpecies = '',
  ownerId = '',
  ownerName = '',
  ownerRut = '',
  fallbackItems = [], // [{ nombre, cantidad, precioUnitario(bruto), tipoPrestacion, unidadNegocio, arancelId }]
  pago = {},          // { paymentMethod, boletaNumber, voucherNumber, last4Digits, paymentNotes }
  branchId = '',
  user = {},          // { uid, name, email }
  orderExtraUpdate = {}, // campos extra a actualizar en la orden (ej. status: 'pending_upload')
}) {
  // El presupuesto manda: sus ítems ya traen los descuentos de convenio
  let presupuesto = null;
  if (presupuestoId) {
    try {
      const snap = await getDoc(doc(db, 'presupuestos', presupuestoId));
      if (snap.exists()) presupuesto = { id: snap.id, ...snap.data() };
    } catch (e) {
      console.error('No se pudo cargar el presupuesto asociado:', e);
    }
  }

  const tipoItem = TIPO_ITEM_BY_ORDER_TYPE[orderType] || 'otro';
  const items = presupuesto
    ? (presupuesto.items || []).map((it) => {
        const cantidad = Number(it.cantidad) || 1;
        const sinDesc = (Number(it.precioUnitario) || 0) * cantidad;
        const total = it.total != null ? Number(it.total) : sinDesc - (Number(it.descuento) || 0);
        return {
          tipo: tipoItem,
          descripcion: it.prestacion || it.descripcion || '',
          cantidad,
          precioUnitario: Number(it.precioUnitario) || 0,
          descuento: sinDesc - total,
          subtotal: netoDesdeBruto(total),
          iva: ivaDesdeBruto(total),
          total,
          referenceId: orderId || presupuesto.id,
          referenceType: orderType || 'presupuesto',
          arancelId: it.arancelId || null,
          unidadNegocio: it.unidadNegocio || '',
          tipoPrestacion: it.tipoPrestacion || '',
        };
      })
    : fallbackItems.map((it) => {
        const cantidad = Number(it.cantidad) || 1;
        const total = (Number(it.precioUnitario) || 0) * cantidad;
        return {
          tipo: tipoItem,
          descripcion: it.nombre || '',
          cantidad,
          precioUnitario: Number(it.precioUnitario) || 0,
          descuento: 0,
          subtotal: netoDesdeBruto(total),
          iva: ivaDesdeBruto(total),
          total,
          referenceId: orderId || null,
          referenceType: orderType || null,
          arancelId: it.arancelId || null,
          unidadNegocio: it.unidadNegocio || '',
          tipoPrestacion: it.tipoPrestacion || '',
        };
      });

  const totalFinal = items.reduce((s, it) => s + it.total, 0);
  const descuentoTotal = items.reduce((s, it) => s + it.descuento, 0);
  const subtotalNeto = netoDesdeBruto(totalFinal);
  const montoIVA = ivaDesdeBruto(totalFinal);

  const branchFinal = branchId || presupuesto?.branchId || '';
  let turno = null;
  try {
    turno = await getTurnoActivo({ branchId: branchFinal });
  } catch (_) { /* sin turno abierto */ }

  const medio = MEDIO_MAP[pago.paymentMethod] || pago.paymentMethod || 'otro';

  const batch = writeBatch(db);
  const ventaRef = doc(collection(db, 'Sales'));
  batch.set(ventaRef, {
    // Referencias de trazabilidad
    presupuestoId: presupuesto?.id || null,
    presupuestoNumber: presupuesto?.numero || '',
    orderId: orderId || null,
    orderType: orderType || '',
    orderNumber: orderNumber || '',
    // Paciente / tutor / convenio
    patientId,
    patientName,
    patientSpecies,
    ownerId: ownerId || presupuesto?.ownerId || '',
    ownerName: ownerName || presupuesto?.ownerName || '',
    ownerRut: ownerRut || '',
    convenioId: presupuesto?.convenioId || '',
    convenioName: presupuesto?.convenioName || '',
    // Sucursal / turno de caja
    branchId: branchFinal,
    turnoId: turno?.id || null,
    cajaId: turno?.cajaId || null,
    cajaNombre: turno?.cajaNombre || '',
    // Ítems y montos (esquema unificado con TicketVenta)
    items,
    subtotal: subtotalNeto,
    iva: montoIVA,
    total: totalFinal,
    subtotalNeto,
    montoIVA,
    montoExento: 0,
    descuentoTotal,
    totalFinal,
    // Documento / pago
    tipoDocumento: 'boleta',
    folioDocumento: pago.boletaNumber || '',
    folioSII: null,
    rutEmpresa: '',
    razonSocial: '',
    paymentMethod: pago.paymentMethod || '',
    boletaNumber: pago.boletaNumber || '',
    voucherNumber: pago.voucherNumber || '',
    last4Digits: pago.last4Digits || '',
    paymentNotes: pago.paymentNotes || '',
    pagos: [{ medio, monto: totalFinal, referencia: pago.voucherNumber || pago.boletaNumber || null, cuotas: null }],
    vuelto: 0,
    estado: 'emitida',
    anulacionMotivo: null,
    anulacionAt: null,
    anulacionBy: null,
    paidAt: serverTimestamp(),
    paidBy: user.uid || '',
    paidByName: user.name || user.email || '',
    createdAt: serverTimestamp(),
  });

  // Actualizar la orden de origen
  if (orderId && orderCollection) {
    batch.update(doc(db, orderCollection, orderId), {
      paymentStatus: 'pagado',
      paymentId: ventaRef.id,
      paidAt: serverTimestamp(),
      ...orderExtraUpdate,
    });
  }

  // Marcar presupuesto como cobrado
  if (presupuesto) {
    batch.update(doc(db, 'presupuestos', presupuesto.id), {
      estado: 'cobrado',
      ventaId: ventaRef.id,
      updatedAt: serverTimestamp(),
      ...(presupuesto.branchId ? {} : { branchId: branchFinal }),
    });
  }

  // Acumular en el turno de caja abierto (si existe)
  if (turno) {
    const campoMedio = CAMPO_TURNO_POR_MEDIO[medio] || 'totalOtros';
    batch.update(doc(db, 'turnosCaja', turno.id), {
      [campoMedio]: increment(totalFinal),
      totalBruto: increment(totalFinal),
      totalNeto: increment(subtotalNeto),
      totalIVA: increment(montoIVA),
      totalDescuentos: increment(descuentoTotal),
      conteoVentas: increment(1),
    });
  }

  await batch.commit();
  return { id: ventaRef.id, total: totalFinal, descuentoTotal };
}
